import { useDispatch } from 'react-redux';
import React, { useCallback } from 'react';
import { identity, prop } from 'ramda';
import { useDeviceSelector, useGameConfigurationSelector, useUserSelector } from 'js/store/hooks';
import { ManagedPopup } from 'js/components/common/Popup';
import { Game as CommonGame } from 'js/components/common/Game';
import { WebContentInner } from 'js/components/system/WebContent';
import { DISPLAY_MODE, GAME_SIZES } from 'js/components/common/GamesView/constants';
import { getOkButton } from 'js/components/common/popups/CommonPopup';
import GridPagingSlider from 'js/components/common/GamesView/GridPagingSlider';
import { updatePopupAction, useOnClose } from 'js/components/common/PopupManager';
import { getAcceptDeclineButtons } from 'js/components/common/popups/ConfirmationPopup';
import { updatePeriod as updateBonusHistoryPeriod } from 'js/components/main/BonusHistory/store/actions';

import * as helpers from './helpers';
import { acceptBonusMessage } from './server';

import style from './BonusWithGamesPopup.scss';

const SLIDER_OPTIONS_MINI = {
    animation: {
        type: 'monoSlider'
    },
    controls: {
        arrows: { enabled: true },
        bullets: { enabled: false },
        buttons: { enabled: false }
    }
};
const Game = (props) => <CommonGame {...props} showTitle={true} container="post-bonus-popup" />;

export const BonusWithGamesPopup = ({
    id,
    title,
    message,
    dialogId,
    controls,
    isAccepted,
    rawBonusDetails,
    isConfirmation,
    syncActionsOnClose = identity,
    useDescription = identity,
    showGamesList
}) => {
    const closeManagedPopup = useOnClose();
    const games = useGameConfigurationSelector(prop('games'));
    const lang = useUserSelector(prop('lang'));
    const bonusDetails = helpers.parseBonus({ data: rawBonusDetails, games, lang });
    const selectedGames = bonusDetails.bonusTemplate?.gamingContext?.selectedGames || [];
    const isModal = isConfirmation && !isAccepted;
    const dispatch = useDispatch();
    const portalDescription = useDescription();
    const isDesktop = useDeviceSelector(prop('isDesktop'));

    const handleClosePopup = useCallback(() => {
        closeManagedPopup();
        dispatch(updateBonusHistoryPeriod());
    }, [closeManagedPopup, dispatch]);

    const closeManagedAndSync = useCallback(() => {
        syncActionsOnClose();
        handleClosePopup();
    }, [handleClosePopup, syncActionsOnClose]);

    const onAcceptClick = useCallback(async () => {
        await acceptBonusMessage({ accept: true, dialogId });
        syncActionsOnClose();

        if (Number(showGamesList)) {
            dispatch(updatePopupAction({ id, isAccepted: true, isModal: false }));
        } else {
            handleClosePopup();
        }
    }, [dialogId, id, syncActionsOnClose, dispatch, handleClosePopup, showGamesList]);

    const onDeclineClick = useCallback(async () => {
        await acceptBonusMessage({ accept: false, dialogId });
        closeManagedAndSync();
    }, [closeManagedAndSync, dialogId]);

    const onOkClick = useCallback(() => {
        if (Number(showGamesList)) {
            dispatch(updatePopupAction({ id, isAccepted: true, isModal: false }));
        } else {
            handleClosePopup();
        }

    }, [id, dispatch, handleClosePopup, showGamesList]);

    const acceptDeclineButtons = getAcceptDeclineButtons({ onAcceptClick, onDeclineClick });

    const buttons = isConfirmation && !isAccepted
        ? acceptDeclineButtons
        : getOkButton(isAccepted ? closeManagedAndSync : onOkClick);

        return (
            <ManagedPopup
                id={id}
                title={title}
                isModal={isModal}
                buttons={buttons}
                controls={controls}
                layerClassNames={style}
                onClose={handleClosePopup}
            >
                {isAccepted
                    ? <>
                        {portalDescription}
                        <GridPagingSlider
                            Game={Game}
                            swipe={!isDesktop}
                            gamesPerSlideMin={1}
                            fillLastSlide={false}
                            showFeatureIcons={false}
                            gameCodes={selectedGames}
                            showControlsOnHover={false}
                            displayMode={DISPLAY_MODE.PAGING}
                            gameSize={GAME_SIZES.EXTRA_SMALL}
                            classNames={{ game: style.game }}
                            sliderOptions={SLIDER_OPTIONS_MINI}
                            container="post-bonus-popup"
                        />
                    </>
                    : <WebContentInner content={message} />
                }
            </ManagedPopup>
        );
};
